import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { availableLanguages } from '../constants/languages';
import { StorageKey } from '../hooks/usePersistedState';

export interface I18nResources {
  [lang: string]: {
    translation: Record<string, unknown>;
  };
}

export type LoadI18nLanguage = (language: string) => Promise<Record<string, unknown> | undefined>;

export interface InitI18nOptions {
  resources: I18nResources;
  defaultLanguage?: string;
  loadLanguage?: LoadI18nLanguage;
}

let resources: I18nResources = {};
let loadLanguage: LoadI18nLanguage | undefined;

const getLanguageCode = (language?: string | null): string | null => {
  const code = language?.split('-')[0];
  if (code && (availableLanguages as readonly string[]).includes(code)) {
    return code;
  }

  return null;
};

const browserLanguage =
  typeof navigator !== 'undefined' ? getLanguageCode(navigator.language) : null;

const getStoredLanguage = (): string | null => {
  try {
    const stored = localStorage.getItem(StorageKey.Language);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'string') {
        return getLanguageCode(parsed);
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
};

const getSelectedLanguage = (defaultLanguage?: string): string =>
  getLanguageCode(defaultLanguage) || getStoredLanguage() || browserLanguage || 'en';

const hasLanguageResource = (language: string): boolean =>
  Boolean(resources[language]?.translation) ||
  (i18n.isInitialized && i18n.hasResourceBundle(language, 'translation'));

const addLanguageResource = (language: string, translation: Record<string, unknown>) => {
  resources[language] = { translation };

  if (i18n.isInitialized) {
    i18n.addResourceBundle(language, 'translation', translation, true, true);
  }
};

const loadLanguageResource = async (language: string): Promise<boolean> => {
  if (hasLanguageResource(language)) {
    return true;
  }

  if (!loadLanguage) {
    return false;
  }

  try {
    const translation = await loadLanguage(language);
    if (!translation) {
      return false;
    }

    addLanguageResource(language, translation);
    return true;
  } catch {
    return false;
  }
};

const ensureLanguageResources = async (language: string): Promise<string> => {
  if (language === 'en') {
    return 'en';
  }

  return (await loadLanguageResource(language)) ? language : 'en';
};

const initI18nextInstance = (language: string) => {
  i18n.use(initReactI18next).init({
    resources,
    fallbackLng: 'en',
    lng: language,
    interpolation: {
      escapeValue: false,
    },
    saveMissing: false,
  });
};

export const changeI18nLanguage = async (language: string): Promise<string> => {
  const selectedLanguage = getLanguageCode(language) || 'en';
  const loadedLanguage = await ensureLanguageResources(selectedLanguage);

  await i18n.changeLanguage(loadedLanguage);

  return loadedLanguage;
};

export const initI18n = async (options: InitI18nOptions) => {
  resources = {
    ...resources,
    ...options.resources,
  };
  loadLanguage = options.loadLanguage;

  const selectedLanguage = getSelectedLanguage(options.defaultLanguage);

  if (i18n.isInitialized) {
    return changeI18nLanguage(selectedLanguage);
  }

  const hasInitialResources =
    selectedLanguage === 'en'
      ? hasLanguageResource('en')
      : hasLanguageResource('en') && hasLanguageResource(selectedLanguage);

  if (hasInitialResources) {
    initI18nextInstance(selectedLanguage);
    return Promise.resolve();
  }

  const loadedLanguage = await ensureLanguageResources(selectedLanguage);

  initI18nextInstance(loadedLanguage);
};

export { i18n };
